import { Component, Inject, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ModalDialogData } from '../../services/carrier-orders-grid/grid-modal-dispatcher.service';
import { CarrierOrdersGridService } from '../../services/carrier-orders-grid/carrier-orders-grid.service';

interface AppointmentWindowValue {
  start: string | null;
  end: string | null;
}

@Component({
  selector: 'app-appointment-window-modal',
  templateUrl: './appointment-window-modal.component.html',
  styleUrls: ['./appointment-window-modal.component.scss'],
})
export class AppointmentWindowModalComponent implements OnInit {
  startValue = '';
  endValue = '';
  saving = false;

  private initialStart: string | null = null;
  private initialEnd: string | null = null;

  constructor(
    private cwoService: CarrierOrdersGridService,
    private dialogRef: MatDialogRef<AppointmentWindowModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDialogData,
  ) {}

  ngOnInit(): void {
    const windowValue = this.parseWindowValue(this.data.rowData?.['__appointment_window']);
    this.initialStart = this.normalizeDateTimeValue(windowValue.start);
    this.initialEnd = this.normalizeDateTimeValue(windowValue.end);
    this.startValue = this.toDateTimeLocal(this.initialStart);
    this.endValue = this.toDateTimeLocal(this.initialEnd);
  }

  get dialogTitle(): string {
    return this.data.modalConfig.dialog_title ?? this.data.headerName ?? 'Scheduled Delivery Window';
  }

  get orderLabel(): string {
    return [
      this.data.rowData?.['v_ladings.alternate_reference'],
      this.data.rowData?.['v_ladings.customer_po'],
      this.data.rowData?.['v_ladings.consignee_name'],
    ].filter(Boolean).join(' • ') || this.data.lading_uuid;
  }

  get isValid(): boolean {
    if (!this.startValue && !this.endValue) return true;
    if (!(this.startValue && this.endValue)) return false;
    return this.endValue >= this.startValue;
  }

  get validationMessage(): string {
    if (!(this.startValue && this.endValue)) {
      return 'Enter both the start and end of the delivery window, or clear it.';
    }

    return 'The delivery window end needs to be after the start.';
  }

  get hasChanges(): boolean {
    const nextStart = this.fromDateTimeLocal(this.startValue);
    const nextEnd = this.fromDateTimeLocal(this.endValue);
    return nextStart !== this.initialStart || nextEnd !== this.initialEnd;
  }

  clearWindow(): void {
    this.startValue = '';
    this.endValue = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  saveWindow(): void {
    if (!this.isValid || this.saving) return;

    if (!this.hasChanges) {
      this.toastr.info('No delivery window changes to save.');
      this.dialogRef.close();
      return;
    }

    const appointmentStart = this.fromDateTimeLocal(this.startValue);
    const appointmentEnd = this.fromDateTimeLocal(this.endValue);

    this.saving = true;

    forkJoin([
      this.cwoService.updateDynamicWorkOrderCell({
        action: 'update_cell',
        entity_target: 'work_orders',
        identity_scope: 'carrier',
        row_uuid: this.data.lading_uuid,
        db_field: 'v_ladings.appointment_start',
        new_value: appointmentStart,
        contact_uuid: this.data.contact_uuid || undefined,
      }),
      this.cwoService.updateDynamicWorkOrderCell({
        action: 'update_cell',
        entity_target: 'work_orders',
        identity_scope: 'carrier',
        row_uuid: this.data.lading_uuid,
        db_field: 'v_ladings.appointment_end',
        new_value: appointmentEnd,
        contact_uuid: this.data.contact_uuid || undefined,
      }),
    ]).subscribe({
      next: ([startResp, endResp]) => {
        this.saving = false;

        const startSuccess = this.isSuccess(startResp);
        const endSuccess = this.isSuccess(endResp);

        if (!startSuccess || !endSuccess) {
          this.toastr.error(this.errorMessage(startResp, endResp));
          return;
        }

        this.toastr.success('Scheduled delivery window updated.');
        this.dialogRef.close({ refreshGrid: true });
      },
      error: (error) => {
        this.saving = false;
        console.error('Appointment window update failed:', error);
        this.toastr.error('Network error saving the delivery window.');
      },
    });
  }

  private parseWindowValue(raw: unknown): AppointmentWindowValue {
    if (!raw) return { start: null, end: null };

    if (typeof raw === 'object') {
      return {
        start: this.normalizeDateTimeValue((raw as any).start),
        end: this.normalizeDateTimeValue((raw as any).end),
      };
    }

    if (typeof raw !== 'string') return { start: null, end: null };

    try {
      const parsed = JSON.parse(raw);
      return {
        start: this.normalizeDateTimeValue(parsed?.start),
        end: this.normalizeDateTimeValue(parsed?.end),
      };
    } catch {
      return { start: null, end: null };
    }
  }

  private toDateTimeLocal(value: string | null): string {
    if (!value) return '';

    const date = this.datePart(value);
    const time = this.timePart(value);
    return date && time ? `${date}T${time}` : '';
  }

  private fromDateTimeLocal(value: string): string | null {
    const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    return match ? `${match[1]} ${match[2]}:00` : null;
  }

  private normalizeDateTimeValue(value: any): string | null {
    if (value === null || value === undefined || value === '') return null;

    const date = this.datePart(value);
    const time = this.timePart(value);
    return date && time ? `${date} ${time}:00` : String(value);
  }

  private datePart(value: any): string {
    const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  private timePart(value: any): string {
    const match = String(value || '').trim().match(/(?:^|\s|T)(\d{2}:\d{2})/);
    return match ? match[1] : '';
  }

  private isSuccess(resp: any): boolean {
    return !!resp && (resp.success === true || resp.code === 200 || resp.code === '200');
  }

  private errorMessage(startResp: any, endResp: any): string {
    if (!this.isSuccess(startResp)) {
      return startResp?.message || 'Could not update the delivery window start.';
    }

    if (!this.isSuccess(endResp)) {
      return endResp?.message || 'Could not update the delivery window end.';
    }

    return 'Could not update the delivery window.';
  }
}
